// ContactStatusPill.tsx — Kontaktstatus im Kopf der Detailseite (User
// 28.09.2026):
//
//   [ Interessent ]
//   [ Kunde  Verantwortung fürs Unternehmen · Müller GmbH  +2 ▾ ]
//   [ Sonstige +1 ▾ ]
//   [ ohne Status ]
//
// Hauptstatus (Kunde / Interessent / Sonstige), von den Rollen NUR
// „Verantwortung fürs Unternehmen" ausgeschrieben; „Mensch im Unternehmen"
// und „Haushaltsmitglied" nie, jede weitere Rolle nur als „+N" (Tooltip nennt
// alle). Klick öffnet `KontaktstatusPopup` — dasselbe Popup wie der
// Spaltenfilter der Kontaktliste — und speichert Hauptstatus + Rollen in einem
// Zug. Anzeige-Logik in `kontaktstatusWahl.ts` (`pillenAnzeige`).
//
// Die Firmennamen am VfU-Teil kommen aus den bereits geladenen Relationen
// (derivePersonEmployment) und erscheinen erst ab Desktop-Breite — auf schmalen
// Geräten würde die Kopfzeile sonst umbrechen (CSS in contactRedesign.css).

import * as React from "react";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";
import type { RollenBereich } from "../../data/kontaktRollen";
import { useKontaktRollen } from "../../services/useKontaktRollen";
import { KontaktstatusPopup } from "./KontaktstatusPopup";
import {
  pillenAnzeige,
  rollenLabel,
  wahlAusKontakt,
  wahlFuerApi,
  type KontaktstatusWahl,
} from "./kontaktstatusWahl";
import { aktivesFenster } from "../windowManager/browserFenster/fensterRegister";

/** Sprungziel eines Nebenstatus-Segments (nur noch als Prop-Typ, s. `onJump`). */
export type StatusJumpTarget = "haushalt" | "verbindungen";

const chipClassFor = (haupt?: string | null): string =>
  haupt === "Kunde" ? "chip-kunde" : haupt === "Interessent" ? "chip-interessent" : "chip-sonstige";

export interface ContactStatusPillProps {
  /** Primär-Status (Contact.status). Leer → „ohne Status". */
  status?: string;
  /** Zusatz-Status aus ContactStatusLink (ohne den Primär-Status). */
  extraStatuses?: string[];
  /** Firmennamen für das „Verantwortung fürs Unternehmen"-Segment (Desktop). */
  companies?: string[];
  /** Darf der Kontaktstatus geändert werden? (Backend prüft zusätzlich.) */
  editable?: boolean;
  /** Kontakttyp — steuert, welche Rollen das Popup anbietet. */
  istFirma?: boolean;
  /** Recht `cointern:read` — schaltet CO-intern-Rollen (Lieferant) frei. */
  coIntern?: boolean;
  /**
   * T1: „persönlicher Kontakt". Reine ANZEIGE — das Feld entsteht bei der
   * Anlage und ist danach unveränderlich (PUT/PATCH strippen es). Ohne diese
   * Anzeige könnte niemand erklären, warum Kolleginnen und Kollegen den
   * Kontakt nicht finden.
   */
  persoenlich?: boolean;
  /**
   * Neuer Kontaktstatus gewählt — in der Speicherform der Anlage
   * (`PUT /contact/:id/kontaktstatus`). Ein zurückgegebenes Promise hält das
   * Popup im Zustand „Speichert …" offen.
   */
  onChangeStatus?: (next: { statusName: string; weitereStatus: string[] }) => void | Promise<unknown>;
  /** Früher: Klick auf ein Nebenstatus-Segment. Seit 28.09.2026 öffnet jeder
   *  Klick auf die Pille das Popup; der Parameter bleibt für Aufrufer stehen. */
  onJump?: (target: StatusJumpTarget) => void;
}

/**
 * Die Rollen, die das Popup der Akte anbietet: Katalog nach Recht (CO intern)
 * und passend zum Kontakttyp. Bereits getragene Rollen, die hier fehlen,
 * zeigt das Popup selbst unter „Weitere Rollen".
 */
export function bereicheFuerAkte(katalog: RollenBereich[], istFirma: boolean, coIntern: boolean): RollenBereich[] {
  const typ = istFirma ? "firma" : "person";
  return katalog
    .map((b) => ({
      ...b,
      rollen: b.rollen.filter((r) => (coIntern || !r.nurCoIntern) && (r.typ === "beide" || r.typ === typ)),
    }))
    .filter((b) => b.rollen.length > 0);
}

export function ContactStatusPill({
  status,
  extraStatuses,
  companies,
  editable,
  istFirma,
  coIntern,
  persoenlich,
  onChangeStatus,
}: ContactStatusPillProps) {
  const [menuAt, setMenuAt] = React.useState<{ x: number; y: number } | null>(null);
  const [speichert, setSpeichert] = React.useState(false);
  const btnRef = React.useRef<HTMLButtonElement>(null);
  const katalog = useKontaktRollen();

  const anzeige = pillenAnzeige(status, extraStatuses);
  const label = anzeige.haupt ?? "ohne Status";
  const namen = anzeige.vfu ? (companies ?? []) : [];
  const alleRollen = anzeige.rollen.map(rollenLabel);

  const openMenu = () => {
    if (!editable) return;
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    const breite = 340;
    const x = Math.max(8, Math.min(r.left, aktivesFenster().innerWidth - breite - 8));
    setMenuAt({ x, y: r.bottom + 6 });
  };

  const speichern = async (w: KontaktstatusWahl) => {
    const body = wahlFuerApi(w);
    if (!body) return;
    try {
      setSpeichert(true);
      await onChangeStatus?.(body);
      setMenuAt(null);
    } finally {
      setSpeichert(false);
    }
  };

  const titel =
    (editable ? "Kontaktstatus ändern" : "Kontaktstatus") +
    (alleRollen.length ? ` — Rollen: ${alleRollen.join(", ")}` : "") +
    (namen.length ? ` — ${namen.join(", ")}` : "");

  return (
    <span className={"chip statuspill " + chipClassFor(anzeige.haupt)}>
      {persoenlich && (
        <span
          className="statuspill-seg static statuspill-persoenlich"
          title="Persönlicher Kontakt: Nur Sie und ein Administrator sehen ihn. Das lässt sich nachträglich nicht ändern."
        >
          {/* Ohne Schloss davor: es las sich wie „gesperrt“ und warf die Frage
              auf, was hier abgeschlossen sei (T-310, 13.10.2026). Das Wort und
              der Tooltip sagen es allein. */}
          Persönlich
        </span>
      )}
      <button
        ref={btnRef}
        type="button"
        className={"statuspill-seg" + (editable ? "" : " static")}
        onClick={openMenu}
        title={titel}
        aria-haspopup={editable ? "dialog" : undefined}
      >
        <span className="cd" />
        {label}
        {anzeige.vfu && (
          <span className="statuspill-rolle">
            Verantwortung fürs Unternehmen
            {namen.length > 0 && (
              <span className="statuspill-firmen">
                {namen[0]}
                {namen.length > 1 ? ` +${namen.length - 1}` : ""}
              </span>
            )}
          </span>
        )}
        {anzeige.mehr > 0 && (
          <span className="statuspill-mehr" aria-label={`${anzeige.mehr} weitere Rollen`}>
            +{anzeige.mehr}
          </span>
        )}
        {editable && <span className="statuspill-caret">▾</span>}
      </button>

      {menuAt &&
        createPortal(
          <div className="kvp-rd-scrim statuspill-scrim" onClick={() => !speichert && setMenuAt(null)}>
            <div
              className="rd-popover kst-akte-pop"
              style={{ left: menuAt.x, top: menuAt.y }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="rd-pop-body">
                <KontaktstatusPopup
                  modus="akte"
                  start={wahlAusKontakt(status, extraStatuses)}
                  bereiche={bereicheFuerAkte(katalog, istFirma === true, coIntern === true)}
                  speichert={speichert}
                  onAnwenden={(w) => void speichern(w)}
                  onAbbrechen={() => setMenuAt(null)}
                />
              </div>
            </div>
          </div>,
          document.body,
        )}
    </span>
  );
}
